"use client";

import { useRef, type ReactNode } from "react";
import Link from "next/link";
import { ChevronLeft, CircleUserRound } from "lucide-react";
import { useNavigation } from "@/lib/navigation";
import { useSession } from "@/lib/session";
import { MENU_PATH, TAB_PATHS } from "@/lib/address";
import { BrandMark } from "@/components/shell/BrandMark";
import { Avatar } from "@/components/ui/Avatar";
import { Skeleton } from "@/components/ui/Spinner";
import { openLogin } from "@/components/auth/login";
import { PhoneBell } from "@/components/notifications/Bell";

/**
 * The bar across the top of a phone's screen. On a tab it carries the brand
 * and, at its end, the bell signed in (montanhas-web#13) and the profile icon
 * that opens the menu (montanhas-web#14): the
 * client's photo signed in, a plain person -- and Entrar -- signed out. On a page
 * stacked over a tab the way back, the page's title and, so the brand never
 * leaves the screen, the badge at its end. `actions` sit before the badge. On a
 * desktop the bar is not drawn (the top bar is), unless `desktop` asks for a
 * slim title bar there too.
 *
 * `back` is where the arrow goes when there is nothing of ours behind: the
 * tab's own address, by default Início.
 */
export function PageHeader({
  title,
  back = TAB_PATHS.home,
  actions,
  transparent = false,
  desktop = false,
}: {
  title?: ReactNode;
  back?: string | null;
  actions?: ReactNode;
  transparent?: boolean;
  desktop?: boolean;
}) {
  const { goBack, stacked } = useNavigation();
  const showBack = stacked && back !== null;

  return (
    // phone-page-header: a layer of its own while a route transition runs (globals.css)
    <header
      className={`phone-page-header sticky z-30 ${desktop ? "" : "desk:hidden"} ${transparent ? "" : "border-b border-line bg-surface/92 backdrop-blur-md"}`}
      // under the live trail strip while it shows (globals.css)
      style={{ top: "var(--strip-h)", paddingTop: "var(--top-inset)" }}
    >
      <div className="flex items-center gap-1 px-2" style={{ height: "var(--topbar-h)" }}>
        {showBack ? (
          <button
            type="button"
            onClick={() => goBack(back || TAB_PATHS.home)}
            className="pressable -ml-0.5 flex h-10 w-10 items-center justify-center rounded-full text-ink"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2.4} />
          </button>
        ) : (
          <BrandMark className="pl-2" />
        )}
        {showBack && <h1 className="min-w-0 flex-1 truncate text-[17px] font-bold text-ink">{title}</h1>}
        {!showBack && <span className="flex-1" />}
        {actions && <div className="flex items-center gap-1">{actions}</div>}
        {showBack && !actions && <BrandMark withName={false} size={28} className="pr-2" />}
        {!showBack && <PhoneBell />}
        {!showBack && <MenuButton />}
      </div>
    </header>
  );
}

/** the way into the menu, at the end of a tab's bar: the client's photo, or a plain person and Entrar */
function MenuButton() {
  const { client, likelySignedIn } = useSession();
  return (
    <div className="flex items-center gap-0.5">
      {!likelySignedIn && (
        <button type="button" onClick={() => openLogin()} className="pressable mr-1 h-[34px] rounded-full bg-brand px-3.5 text-[13.5px] font-bold text-white">
          Entrar
        </button>
      )}
      <Link href={MENU_PATH} className="pressable flex h-10 w-10 items-center justify-center rounded-full text-ink" aria-label={client ? "A minha conta" : "Menu"}>
        {client ? (
          <Avatar photo={client.profile_photo_thumbnail || client.profile_photo} name={client.nickname || client.name} size={32} />
        ) : likelySignedIn ? (
          <Skeleton className="h-8 w-8 rounded-full" />
        ) : (
          <CircleUserRound size={26} strokeWidth={1.9} />
        )}
      </Link>
    </div>
  );
}

/**
 * A phone's page stacked over a tab: a swipe in from the left edge goes back,
 * as a native stack does. The content is unaffected by the gesture; only the
 * strip along the edge listens.
 */
export function StackedPage({ children, back = TAB_PATHS.home }: { children: ReactNode; back?: string }) {
  const { goBack } = useNavigation();
  const start = useRef<number | null>(null);

  return (
    // as tall as #page on a desktop, for the footer to sit at the bottom of (SiteFooter)
    <div className="relative desk:min-h-[inherit]">
      <div
        className="absolute inset-y-0 left-0 z-20 w-5 desk:hidden"
        style={{ touchAction: "pan-y" }}
        onPointerDown={(e) => {
          start.current = e.clientX;
        }}
        onPointerMove={(e) => {
          if (start.current !== null && e.clientX - start.current > 70) {
            start.current = null;
            goBack(back);
          }
        }}
        onPointerUp={() => {
          start.current = null;
        }}
        onPointerCancel={() => {
          start.current = null;
        }}
      />
      {children}
    </div>
  );
}
